import { t } from 'i18next';
import { SeeqNames } from '@/main/app.constants.seeqnames';
import { getTableDefinitionStore } from '@/tableDefinitionEditor/store.utilities';
import {
  setDoTableReload,
  setEvaluationResults,
  setRequestProgress,
  setRowDataIsLoading,
  setScrollToColumnId,
  setSelectedColumns,
  setTableDefinition,
} from '@/tableDefinitionEditor/tableDefinition.actions';
import { CSV_TYPES, EDITOR_TYPES } from '@/tableDefinitionEditor/tableDefinition.constants';
import {
  addOrUpdateColumnDefinition,
  addOrUpdateColumnDefinitions,
} from '@/tableDefinitionEditor/tableDefinition.utilities';
import {
  setColumnToEditId,
  setInitialColumnRule,
  setSelectedColumnOption,
  setShowColumnProperties,
} from '@/tableDefinitionEditor/tableDefinitionEditor.actions';
import { doTrack } from '@/track/trackProxy.service';
import { ColumnTypeEnum } from '@seeqdev/api/model/ColumnDefinitionBulkEditInputV1';
import type { EditorType } from '@/tableDefinitionEditor/tableDefinition.types';
import type { TrackInformation } from '@/track/track.types';
import type { ColumnDefinitionBulkEditInputV1 } from '@seeqdev/api/model/ColumnDefinitionBulkEditInputV1';
import type { ColumnDefinitionInputV1 } from '@seeqdev/api/model/ColumnDefinitionInputV1';

const columnConfigurationTrackCategory = (editorType: EditorType) =>
  `${editorType === EDITOR_TYPES.SCALING ? 'Scaling' : 'Item'} Table Column Configuration`;

export const resetRowLoadProgressAndSetRowLoading = (tableDefinitionId: string, editorType: EditorType) => {
  setRequestProgress(editorType, {
    id: tableDefinitionId,
    progress: '0' as unknown as Record<string, unknown>,
    meterInformation: undefined,
    timingInformation: undefined,
  });
  setRowDataIsLoading(editorType, true);
};

function getPropertyAccessorForEventAssetCreator(propertyName?: string): string {
  if (
    !propertyName ||
    propertyName.toUpperCase() === SeeqNames.Properties.Id ||
    propertyName === SeeqNames.Properties.Name
  ) {
    return '';
  }
  const lastDotIndex = propertyName.lastIndexOf('.');
  return lastDotIndex !== -1 ? propertyName.substring(0, lastDotIndex) : propertyName;
}

export const modifyColumnsToIncludeEventAssetCreator = (
  columnInputs: ColumnDefinitionBulkEditInputV1[],
  editorType: EditorType,
) => {
  const tableDefinitionStore = getTableDefinitionStore(editorType);
  const { tableType, name, id } = tableDefinitionStore;
  if (CSV_TYPES.includes(tableType)) {
    return columnInputs;
  }

  const modifiedColumns = columnInputs.map((column) => {
    const isUUIDColumn = column.columnType === ColumnTypeEnum.UUID;
    const onlyOneRule = column.columnRules.length === 1;
    const hasEventPropertyRule = column.columnRules.some((rule) => rule.eventProperty);
    const endsInName =
      onlyOneRule &&
      hasEventPropertyRule &&
      column.columnRules[0].eventProperty?.propertyName.toUpperCase().endsWith(SeeqNames.Properties.Name.toUpperCase());
    const isItemIDColumn = column.columnName === SeeqNames.MaterializedTables.ItemIdColumn;

    const shouldModify = isItemIDColumn || (endsInName && isUUIDColumn && onlyOneRule && hasEventPropertyRule);
    if (!shouldModify) {
      return column;
    }
    const eventPropertyRule = column.columnRules.find((rule) => rule.eventProperty);
    if (eventPropertyRule) {
      const description = id
        ? t('SCALING.RULES.EVENT_ASSET_CREATOR.GENERATED_RULE_DESCRIPTION_WITH_ID', { name, id })
        : t('SCALING.RULES.EVENT_ASSET_CREATOR.GENERATED_RULE_DESCRIPTION');
      const propertyName = getPropertyAccessorForEventAssetCreator(eventPropertyRule.eventProperty?.propertyName) || '';
      return {
        ...column,
        columnRules: [
          ...column.columnRules,
          {
            eventAssetCreator: {
              propertyName: propertyName ?? '',
              description,
            },
          },
        ],
      };
    }
    return column;
  });
  return modifiedColumns;
};
export const addOrUpdateColumns = async (
  columnDefinitions: ColumnDefinitionBulkEditInputV1[],
  tableDefinitionId: string,
  editorType: EditorType,
  skipRecompute?: boolean,
) => {
  resetRowLoadProgressAndSetRowLoading(tableDefinitionId, editorType);
  const columns = modifyColumnsToIncludeEventAssetCreator(columnDefinitions, editorType);
  const output = await addOrUpdateColumnDefinitions(columns, tableDefinitionId, skipRecompute);
  if (output) {
    setTableDefinition(editorType, output.tableDefinition);
    setEvaluationResults(editorType, output.computeResults);
    setDoTableReload(editorType, true);
    setScrollToColumnId(
      editorType,
      output.tableDefinition.columnDefinitions?.find(
        (column) => column.columnName === columns[columns.length - 1].columnName,
      )?.id || '',
    );

    const trackCategory = columnConfigurationTrackCategory(editorType);
    const trackInformation: TrackInformation = {
      numberOfColumns: columns.length,
    };
    doTrack(trackCategory, columns.some((col) => col.columnId) ? 'Edit Columns' : 'Add Columns', trackInformation);
  }
  return output;
};

export const onCloseColumnConfig = (editorType: EditorType) => {
  setSelectedColumnOption(editorType, undefined);
  setColumnToEditId(editorType, undefined);
  setShowColumnProperties(editorType, false);
  setSelectedColumns(editorType, []);
  setInitialColumnRule(editorType, undefined);
};

export const addOrUpdateColumn = async (
  editorType: EditorType,
  tableDefinitionId: string,
  columnAsInput: ColumnDefinitionInputV1,
  columnId?: string,
  skipRuleCompute = false,
  skipTracking = false,
  closeColumnConfig = true,
  includeDetailedResults = true,
) => {
  resetRowLoadProgressAndSetRowLoading(tableDefinitionId, editorType);
  const modifiedInput = modifyColumnsToIncludeEventAssetCreator([columnAsInput], editorType)[0];
  const output = await addOrUpdateColumnDefinition(
    tableDefinitionId,
    modifiedInput,
    columnId,
    skipRuleCompute,
    includeDetailedResults,
  );
  const trackCategory = columnConfigurationTrackCategory(editorType);
  if (output) {
    if (!skipTracking) {
      const trackInformation: TrackInformation = {
        numberOfRules: columnAsInput.columnRules.length,
      };
      columnAsInput.columnRules.forEach((rule) => {
        const ruleName = Object.keys(rule).find((key) => key)!;
        trackInformation[ruleName] = ruleName;
      });
      doTrack(trackCategory, columnId ? 'Edit Column' : 'Add Column', trackInformation);
    }
    setTableDefinition(editorType, output.tableDefinition);
    setEvaluationResults(editorType, includeDetailedResults ? output.computeResults : {});
    setDoTableReload(editorType, true);
    setScrollToColumnId(
      editorType,
      output.tableDefinition.columnDefinitions?.find((column) => column.columnName === columnAsInput.columnName)?.id ||
        '',
    );
    if (closeColumnConfig) {
      onCloseColumnConfig(editorType);
    }
  }
  return output;
};
